import React, { useEffect, useRef, useState } from 'react';
import { getApiEndpoint } from 'utils/centralizedConfig';
import { buildSdkUrl } from 'utils/sdkUrlHelper';
import { useDarkMode } from 'utils/darkMode';
import { logger } from 'utils/logger';
import './DemoSDKContainer.css';

// Dark mode color scheme for demo SDK
const DARK_MODE_COLORS = {
  // Backgrounds
  bgCard: '#1a202c',
  bgTertiary: '#2d3748',
  bgHeader: '#2d3748',
  bgChat: '#1a202c',
  
  // Text colors
  textPrimary: '#e2e8f0',
  textSecondary: '#94a3b8',
  textTertiary: '#718096',
  textInverse: '#ffffff',
  
  // Primary colors
  primary: '#4299e1',
  primaryHover: '#3182ce',
  
  // Borders
  border: '#4a5568',
  
  // Other
  radiusLg: '16px',
};

// Light mode color scheme (default)
const LIGHT_MODE_COLORS = {
  // Backgrounds
  bgCard: '#ffffff',
  bgTertiary: '#f8fafc',
  bgHeader: '#ffffff',
  bgChat: '#ffffff',
  
  // Text colors
  textPrimary: '#1a1f2c',
  textSecondary: '#64748b',
  textTertiary: '#94a3b8',
  textInverse: '#ffffff',
  
  // Primary colors
  primary: '#5897de',
  primaryHover: '#4a85c9',
  
  // Borders
  border: '#e1e7ef',
  
  // Other
  radiusLg: '16px',
};

/**
 * Demo SDK Container Component
 * Wrapper component that loads and manages the <besper-bot-demo> web component
 * Supports dark/light mode styling and custom style injection
 */
const DemoSDKContainer = ({ containerId = 'besper-demo-chat-container', className = '', customStyles = null }) => {
  const containerRef = useRef(null);
  const scriptRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setApiUrl] = useState(null);
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    let mounted = true;

    // Check if mobile - don't load demo SDK on mobile devices
    const checkMobile = () => {
      const isMobileWidth = window.innerWidth <= 768;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isTouchDevice = navigator.maxTouchPoints && navigator.maxTouchPoints > 2;
      
      return isMobileWidth || isMobileUserAgent || isTouchDevice;
    };

    // Don't load on mobile
    if (checkMobile()) {
      logger.info('DEMO-SDK-CONTAINER', 'Mobile device detected - skipping demo SDK load', {
        width: window.innerWidth,
        userAgent: navigator.userAgent,
        touchPoints: navigator.maxTouchPoints
      });
      
      // Remove any existing demo components
      if (containerRef.current) {
        const existingComponent = containerRef.current.querySelector('besper-bot-demo');
        if (existingComponent) {
          existingComponent.remove();
        }
      }
      
      // Also remove from document
      const allDemoComponents = document.querySelectorAll('besper-bot-demo');
      allDemoComponents.forEach(component => {
        component.remove();
      });
      
      setLoading(false);
      return;
    }

    const loadDemoSDK = async () => {
      try {
        // Double-check mobile before loading
        if (checkMobile()) {
          logger.info('DEMO-SDK-CONTAINER', 'Mobile detected during load - aborting');
          setLoading(false);
          return;
        }
        
        // Get API endpoint
        const baseUrl = await getApiEndpoint('public');
        const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
        const API_BASE = isLocalhost ? `${baseUrl}/api` : baseUrl;
        
        setApiUrl(API_BASE);

        // CRITICAL: Set window config BEFORE loading the script
        // So the SDK can find the API base URL when the component initializes
        if (!window.__BESPER_SDK_CONFIG__) {
          window.__BESPER_SDK_CONFIG__ = {};
        }
        const apiBase = API_BASE.replace(/\/api\/?$/, '');
        window.__BESPER_SDK_CONFIG__.api_base_url = apiBase;
        logger.debug('DEMO-SDK-CONTAINER', 'Set API base config', { apiBase, apiBaseFull: API_BASE });

        // Check if script is already loaded
        const existingScript = document.querySelector(`script[data-sdk="bot_demo"]`);
        if (existingScript) {
          // Script already loaded, just initialize the component
          initializeComponent(API_BASE);
          return;
        }

        // Create script tag to load the demo SDK
        const script = document.createElement('script');
        script.type = 'module';
        script.setAttribute('data-sdk', 'bot_demo');
        script.setAttribute('data-container', containerId);
        
        // Use the bot SDK endpoint with bot_demo mode
        // The endpoint format: /api/v1/sdk/bots/{bot_id}/bot_demo
        // For demo SDK, we use "demo" as a placeholder bot_id since it creates its own resource
        const DEMO_BOT_ID = 'demo';
        const sdkUrl = buildSdkUrl(API_BASE, 'bots', DEMO_BOT_ID, 'bot_demo');
        script.src = `${sdkUrl}?v=${Date.now()}`;
        
        script.onload = () => {
          if (mounted) {
            logger.debug('DEMO-SDK-CONTAINER', 'SDK script loaded successfully');
            // Wait a bit for the SDK module to register the custom element
            setTimeout(() => {
              initializeComponent(API_BASE);
            }, 100);
          }
        };

        script.onerror = (err) => {
          logger.error('DEMO-SDK-CONTAINER', 'Failed to load SDK script', { error: err.message });
          if (mounted) {
            setError('Failed to load demo SDK. Please check your connection and try again.');
            setLoading(false);
          }
        };

        document.head.appendChild(script);
        scriptRef.current = script;

      } catch (err) {
        logger.error('DEMO-SDK-CONTAINER', 'Error loading demo SDK', { error: err.message });
        if (mounted) {
          setError('Error initializing demo SDK');
          setLoading(false);
        }
      }
    };

    const initializeComponent = (apiBase) => {
      if (!containerRef.current) return;

      // Check if component already exists
      const existingComponent = containerRef.current.querySelector('besper-bot-demo');
      if (existingComponent) {
        logger.debug('DEMO-SDK-CONTAINER', 'Component already exists');
        setLoading(false);
        return;
      }

      // Set window config for SDK initialization (if not already set)
      if (!window.__BESPER_SDK_CONFIG__) {
        window.__BESPER_SDK_CONFIG__ = {};
      }
      const apiBaseNoPrefix = apiBase.replace(/\/api\/?$/, '');
      if (!window.__BESPER_SDK_CONFIG__.api_base_url) {
        window.__BESPER_SDK_CONFIG__.api_base_url = apiBaseNoPrefix;
        logger.debug('DEMO-SDK-CONTAINER', 'Set API base in initializeComponent', { apiBaseNoPrefix });
      }

      // Check if custom element is registered
      if (!customElements.get('besper-bot-demo')) {
        logger.warn('DEMO-SDK-CONTAINER', 'besper-bot-demo custom element not registered yet, waiting');
        // Wait for custom element to be registered
        const checkInterval = setInterval(() => {
          if (customElements.get('besper-bot-demo')) {
            clearInterval(checkInterval);
            createDemoComponent();
          } else if (!mounted) {
            clearInterval(checkInterval);
          }
        }, 100);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkInterval);
          if (mounted && !containerRef.current.querySelector('besper-bot-demo')) {
            logger.error('DEMO-SDK-CONTAINER', 'Custom element not registered after timeout');
            setError('Demo SDK failed to initialize. Please refresh the page.');
            setLoading(false);
          }
        }, 5000);
      } else {
        createDemoComponent();
      }
      
      function createDemoComponent() {
        if (!containerRef.current || !mounted) return;
        
        // Check if component already exists
        const existingComponent = containerRef.current.querySelector('besper-bot-demo');
        if (existingComponent) {
          logger.debug('DEMO-SDK-CONTAINER', 'Component already exists');
          setLoading(false);
          return;
        }
        
        // Create the web component
        try {
          const demoComponent = document.createElement('besper-bot-demo');
          containerRef.current.appendChild(demoComponent);
          logger.debug('DEMO-SDK-CONTAINER', 'Demo component created successfully');
          
          // Wait for component to initialize
          setTimeout(() => {
            if (mounted) {
              setLoading(false);
            }
          }, 500);
        } catch (error) {
          logger.error('DEMO-SDK-CONTAINER', 'Error creating demo component', { error: error.message });
          if (mounted) {
            setError('Failed to create demo component');
            setLoading(false);
          }
        }
      }
    };

    loadDemoSDK();

    // Set up resize listener to remove demo if window resizes to mobile
    const handleResize = () => {
      if (checkMobile()) {
        const existingComponent = containerRef.current?.querySelector('besper-bot-demo');
        if (existingComponent) {
          existingComponent.remove();
        }
        // Also remove from document
        const allDemoComponents = document.querySelectorAll('besper-bot-demo');
        allDemoComponents.forEach(component => {
          component.remove();
        });
      }
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      mounted = false;
      window.removeEventListener('resize', handleResize);
      // Cleanup: Remove script if we added it
      if (scriptRef.current && scriptRef.current.parentNode) {
        // Don't remove script as it might be used by other instances
        // Just clear the reference
        scriptRef.current = null;
      }
    };
  }, [containerId]);

  // Inject styles based on dark mode and custom styles
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const demoElement = container.querySelector('besper-bot-demo');
    
    if (!demoElement) {
      // Wait for component to be created
      const checkInterval = setInterval(() => {
        const checkDemo = container.querySelector('besper-bot-demo');
        if (checkDemo) {
          clearInterval(checkInterval);
          injectStyles(checkDemo);
        }
      }, 200);
      
      return () => clearInterval(checkInterval);
    }

    injectStyles(demoElement);
  }, [containerId, isDarkMode, customStyles]);

  const injectStyles = (demoElement) => {
    if (!demoElement) return;

    const styleId = `besper-demo-theme-${containerId}`;
    let styleEl = document.getElementById(styleId);
    
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    
    const colors = isDarkMode ? DARK_MODE_COLORS : LIGHT_MODE_COLORS;
    const selector = `#${containerId} besper-bot-demo, besper-bot-demo`;
    
    // Build custom style variables from customStyles prop or use defaults
    const customVars = customStyles || {};
    
    styleEl.textContent = `
      ${selector} {
        /* Typography */
        --besper-demo-fontFamily: ${customVars.fontFamily || '-apple-system, BlinkMacSystemFont, \'Segoe UI\', \'Helvetica Neue\', Arial, sans-serif'};
        
        /* Backgrounds - using besper-demo prefix for customization */
        --besper-demo-bgCard: ${customVars.bgCard || colors.bgCard};
        --besper-demo-bgTertiary: ${customVars.bgTertiary || colors.bgTertiary};
        --besper-demo-bgHeader: ${customVars.bgHeader || colors.bgHeader};
        --besper-demo-bgChat: ${customVars.bgChat || colors.bgChat};
        
        /* Text colors */
        --besper-demo-textPrimary: ${customVars.textPrimary || colors.textPrimary};
        --besper-demo-textSecondary: ${customVars.textSecondary || colors.textSecondary};
        --besper-demo-textTertiary: ${customVars.textTertiary || colors.textTertiary};
        --besper-demo-textInverse: ${customVars.textInverse || colors.textInverse};
        
        /* Primary colors */
        --besper-demo-primary: ${customVars.primary || colors.primary};
        --besper-demo-primaryHover: ${customVars.primaryHover || colors.primaryHover};
        
        /* Borders */
        --besper-demo-border: ${customVars.border || colors.border};
        
        /* Other */
        --besper-demo-radiusLg: ${customVars.radiusLg || colors.radiusLg};
      }
    `;
    
    // Apply dark mode attribute to component
    if (isDarkMode) {
      demoElement.setAttribute('data-darkmode', 'true');
    } else {
      demoElement.removeAttribute('data-darkmode');
    }
    
    logger.debug('DEMO-SDK', `Applied ${isDarkMode ? 'dark' : 'light'} mode theme to demo SDK`);
  };

  return (
    <div className={`demo-sdk-container ${className}`.trim()}>
      <div
        ref={containerRef}
        id={containerId}
        className="demo-sdk-wrapper"
      >
        {loading && !error && (
          <div className="demo-sdk-loading">
            <div className="demo-sdk-spinner"></div>
            <p>Loading demo...</p>
          </div>
        )}
        {error && (
          <div className="demo-sdk-error">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DemoSDKContainer;

